import * as React from "react";
import type { User } from "./presence-schema";
import { usePresenceWithCursors } from "./use-cursors";
import { sharedStyles, isLightColor, getContrastingTextColor, VerificationIcon, SimpleVerificationIcon, isVerifiedDomain } from "./shared-styles";

export const cursorSVGs = [
  // sphere
  <g transform="scale(2)">
    <path stroke="#ffffff" d="M4 0h5M2 1h2M9 1h1M1 2h1M10 2h1M1 3h1M11 3h1M0 4h1M11 4h1M0 5h1M11 5h1M0 6h1M3 6h1M11 6h1M0 7h1M3 7h1M7 7h1M11 7h1M0 8h1M7 8h1M10 8h1M1 9h1M10 9h1M2 10h1M8 10h2M3 11h5" />
    <path stroke="{{fill}}" d="M4 1h5M2 2h8M2 3h9M1 4h10M1 5h10M1 6h1M4 6h7M1 7h1M4 7h2M8 7h3M1 8h5M8 8h2M2 9h8M3 10h5" />
    <path stroke="#323232" d="M2 6h1M2 7h1M6 7h1M6 8h1" />
  </g>,
  // cat
  <g transform="scale(2)">
  <path stroke="#ffffff" d="M3 0h1M8 0h1M2 1h1M4 1h1M7 1h1M9 1h1M1 2h1M5 2h2M10 2h1M1 3h1M10 3h1M0 4h1M10 4h1M0 5h1M11 5h1M0 6h1M3 6h1M11 6h1M0 7h1M3 7h1M7 7h1M11 7h1M0 8h1M7 8h1M11 8h1M1 9h1M10 9h1M2 10h1M8 10h2M3 11h5" />
  <path stroke="{{fill}}" d="M3 1h1M8 1h1M2 2h3M7 2h3M2 3h8M1 4h9M1 5h10M1 6h1M4 6h7M1 7h1M4 7h2M8 7h3M1 8h5M8 8h3M2 9h8M3 10h5" />
  <path stroke="#323232" d="M2 6h1M2 7h1M6 7h1M6 8h1" />
</g>,
  // slime
  <g transform="scale(2)">
  <path stroke="#ffffff" d="M5 0h2M3 1h2M7 1h2M2 2h1M9 2h1M1 3h1M10 3h1M1 4h1M10 4h1M0 5h1M11 5h1M0 6h1M3 6h1M11 6h1M0 7h1M3 7h1M7 7h1M11 7h1M0 8h1M7 8h1M10 8h1M1 9h1M10 9h1M2 10h1M8 10h2M3 11h5" />
  <path stroke="{{fill}}" d="M5 1h2M3 2h6M2 3h8M2 4h8M1 5h10M1 6h1M4 6h7M1 7h1M4 7h2M8 7h3M1 8h5M8 8h2M2 9h8M3 10h5" />
  <path stroke="#323232" d="M2 6h1M2 7h1M6 7h1M6 8h1" />
</g>,
  // cube
  <g transform="scale(2)">
  <path stroke="#ffffff" d="M3 0h7M2 1h1M10 1h1M1 2h1M11 2h1M0 3h1M11 3h1M0 4h1M11 4h1M0 5h1M11 5h1M0 6h1M3 6h1M11 6h1M0 7h1M3 7h1M7 7h1M11 7h1M0 8h1M7 8h1M11 8h1M0 9h1M10 9h1M1 10h1M9 10h1M2 11h7" />
  <path stroke="{{fill}}" d="M3 1h7M2 2h9M1 3h10M1 4h10M1 5h10M1 6h1M4 6h7M1 7h1M4 7h2M8 7h3M1 8h5M8 8h3M1 9h9M2 10h7" />
  <path stroke="#323232" d="M2 6h1M2 7h1M6 7h1M6 8h1" />
</g>,
  // bunny
  <g transform="scale(2)">
  <path stroke="#ffffff" d="M2 0h2M8 0h2M1 1h1M4 1h1M7 1h1M10 1h1M1 2h1M4 2h1M7 2h1M10 2h1M1 3h1M5 3h2M10 3h1M0 4h1M11 4h1M0 5h1M11 5h1M0 6h1M3 6h1M11 6h1M0 7h1M3 7h1M7 7h1M11 7h1M0 8h1M7 8h1M11 8h1M0 9h1M11 9h1M1 10h1M9 10h2M2 11h7" />
  <path stroke="{{fill}}" d="M2 1h2M8 1h2M2 2h2M8 2h2M2 3h3M7 3h3M1 4h10M1 5h10M1 6h1M4 6h7M1 7h1M4 7h2M8 7h3M1 8h5M8 8h3M1 9h10M2 10h7" />
  <path stroke="#323232" d="M2 6h1M2 7h1M6 7h1M6 8h1" />
</g>
];

export function Pointer({ fill, svgIndex }: { fill: string; svgIndex: number }) {
  const svgContent = React.useMemo(() => {
    const svg = cursorSVGs[svgIndex];
    return React.cloneElement(svg, {}, React.Children.map(svg.props.children, child => 
      React.cloneElement(child, { 
        fill: child.props.fill === "{{fill}}" ? fill : child.props.fill,
        stroke: child.props.stroke === "{{fill}}" ? fill : child.props.stroke
      })
    ));
  }, [fill, svgIndex]);

  return (
    <svg
      height="36"
      viewBox="0 0 24 28"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
      style={{ 
        filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.2))',
        willChange: 'transform'
      }}
    >
      <g transform="translate(0, 4)">
        {svgContent}
      </g>
    </svg>
  );
}

export default function Cursor(props: {
  userId: string;
  fill: string;
  showChat: boolean;
}) {
  const user: User | null = usePresenceWithCursors(
    (state) => state.otherUsers.get(props.userId) || null
  );
  const [isMovingRight, setIsMovingRight] = React.useState(false);
  const prevPosition = React.useRef({ x: 0, y: 0 });

  React.useEffect(() => {
    if (user?.presence?.cursor) {
      const currentX = user.presence.cursor.x;
      if (currentX > prevPosition.current.x) {
        setIsMovingRight(true);
      } else if (currentX < prevPosition.current.x) {
        setIsMovingRight(false);
      }
      prevPosition.current = { x: currentX, y: user.presence.cursor.y };
    }
  }, [user?.presence?.cursor]);

  if (!user?.presence?.cursor) return null;

  const cursor = {
    x: user.presence.cursor.x,
    y: user.presence.cursor.y,
    pointer: user.presence.cursor.pointer,
    message: user.presence.message ?? null,
  };

  const isNightMode = !props.showChat;
  const styles = isNightMode
    ? { filter: "blur(1.5px)", opacity: 0.2, zIndex: 300 }
    : cursor.message !== null
      ? { opacity: 1.0, zIndex: 300 }
      : { filter: "blur(1px)", opacity: 0.3, zIndex: -1 };

  const cursorColor = user.presence.color || props.fill;
  const isLight = isLightColor(cursorColor);

  const svgIndex = user.presence.cursorIndex ?? (props.userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % cursorSVGs.length);

  return (
    <div
      key={`${cursor.x}-${cursor.y}`}
      style={{
        position: "absolute",
        transform: `translate(${cursor.x}px, ${cursor.y}px)`,
        ...styles,
      }}
    >
      <div style={{ transform: isMovingRight ? 'scaleX(-1)' : 'none' }}>
        <div style={sharedStyles.cursorWrapper}>
          <Pointer fill={cursorColor} svgIndex={svgIndex} />
        </div>
      </div>
      {cursor.message !== null && (
        <>
          {user.presence.domain && (
            <div
              style={{
                ...sharedStyles.domainNameContainer,
                color: getContrastingTextColor(cursorColor),
                backgroundColor: cursorColor,
              }}>
              <span>{user.presence.domain}</span>
              {isVerifiedDomain(user.presence.domain) ? (
                <VerificationIcon isLight={isLight} color={cursorColor} />
              ) : (
                <SimpleVerificationIcon isLight={isLight} color={cursorColor} />
              )}
            </div>
          )}
          <div
            style={{
              ...sharedStyles.chatBubble,
              color: getContrastingTextColor(cursorColor),
              backgroundColor: cursorColor,
            }}
          >
            {cursor.message}
          </div>
        </>
      )}
    </div>
  );
}
